import { useState } from 'react'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, RotateCcw, Settings2 } from 'lucide-react'
import { Button, Checkbox } from '@/components/ui'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../Popover/Popover'
import type { UseColumnConfigResult } from './useColumnConfig'

/**
 * Gear-icon popover that lists every column with:
 *   - drag handle to reorder (excludes sticky columns)
 *   - checkbox to toggle visibility (excludes sticky columns)
 *
 * Sticky columns render at the top, locked, no drag handle, no
 * checkbox. The Reset button restores the consumer's defaults.
 */
export interface ColumnConfigMenuProps {
  config: UseColumnConfigResult
  triggerLabel?: string
}

export function ColumnConfigMenu({
  config,
  triggerLabel = 'Configure columns',
}: ColumnConfigMenuProps) {
  const [open, setOpen] = useState(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  const movableColumns = config.columns.filter((c) => !c.sticky)
  const stickyColumns = config.columns.filter((c) => c.sticky)
  const movableKeys = movableColumns.map((c) => c.key)

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = movableKeys.indexOf(String(active.id))
    const newIndex = movableKeys.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const reordered = arrayMove(movableKeys, oldIndex, newIndex)
    config.setOrder([...stickyColumns.map((c) => c.key), ...reordered])
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          iconOnly
          aria-label={triggerLabel}
          title={triggerLabel}
        >
          <Settings2 size={14} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-2" align="end">
        <div className="flex items-center justify-between px-2 py-1">
          <span className="text-role-section-label">Columns</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => config.reset()}
            title="Reset to defaults"
          >
            <RotateCcw size={11} />
            Reset
          </Button>
        </div>

        {stickyColumns.length > 0 ? (
          <ul className="border-b border-border-subtle pb-1 mb-1">
            {stickyColumns.map((c) => (
              <li
                key={c.key}
                className="flex items-center gap-2 px-2 py-1 text-sm text-tertiary"
              >
                <span className="w-3.5" />
                <span className="flex-1">{c.label}</span>
                <span className="text-xs">always shown</span>
              </li>
            ))}
          </ul>
        ) : null}

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={movableKeys}
            strategy={verticalListSortingStrategy}
          >
            <ul>
              {movableKeys.map((key) => {
                const def = movableColumns.find((c) => c.key === key)
                if (!def) return null
                return (
                  <SortableRow
                    key={key}
                    id={key}
                    label={def.label}
                    visible={!config.hidden.has(key)}
                    onToggle={(v) => config.setHidden(key, !v)}
                  />
                )
              })}
            </ul>
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  )
}

interface SortableRowProps {
  id: string
  label: string
  visible: boolean
  onToggle: (next: boolean) => void
}

function SortableRow({ id, label, visible, onToggle }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-surface-sunk rounded-xs"
    >
      <button
        type="button"
        aria-label={`Drag ${label}`}
        className="cursor-grab text-tertiary hover:text-primary"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={12} />
      </button>
      <Checkbox
        checked={visible}
        onChange={(e) => onToggle(e.currentTarget.checked)}
      />
      <span className="flex-1">{label}</span>
    </li>
  )
}
